import { API_CONFIG, fetchAPI } from './api';
import type {
  ScrapeSource,
  ScrapeResult,
  StatusResult,
  Company,
} from '@/types';

function normalizeDatasetRef<T extends Record<string, unknown> | undefined>(ref: T): T {
  if (!ref || typeof ref !== 'object') return ref;
  const anyRef = ref as Record<string, unknown>;
  if (!anyRef.dataset_id && anyRef.datasetId) {
    anyRef.dataset_id = anyRef.datasetId;
  }
  return ref;
}

function normalizeScrapeResult(result: ScrapeResult): ScrapeResult {
  const normalized: ScrapeResult = { ...result };
  if (!normalized.dataset_id && (result as any).datasetId) {
    (normalized as any).dataset_id = (result as any).datasetId;
  }
  normalized.company = normalizeDatasetRef(normalized.company as any) as any;
  normalized.posts = normalizeDatasetRef(normalized.posts as any) as any;
  normalized.jobs = normalizeDatasetRef(normalized.jobs as any) as any;
  normalized.overview = normalizeDatasetRef(normalized.overview as any) as any;
  normalized.reviews = normalizeDatasetRef(normalized.reviews as any) as any;
  normalized.salaries = normalizeDatasetRef(normalized.salaries as any) as any;
  normalized.interviews = normalizeDatasetRef(normalized.interviews as any) as any;
  normalized.benefits = normalizeDatasetRef(normalized.benefits as any) as any;
  if (normalized.results) {
    normalized.results.trustpilot = normalizeDatasetRef(normalized.results.trustpilot as any) as any;
    normalized.results.g2 = normalizeDatasetRef(normalized.results.g2 as any) as any;
  }
  return normalized;
}

// Start a scrape job
export async function startScrape(
  source: ScrapeSource,
  companyName: string,
  linkedinUrl?: string,
  userEmail?: string,
  forceRefresh?: boolean,
  extra?: Record<string, unknown>
): Promise<ScrapeResult> {
  const requestBody: Record<string, unknown> = {
    action: 'scrape',
    type: source,
    company: companyName,
    location: 'United States',
  };

  if (source === 'linkedin_360' && linkedinUrl) {
    requestBody.linkedinUrl = linkedinUrl;
  }
  if (userEmail) {
    requestBody.userEmail = userEmail;
  }
  if (forceRefresh) {
    // SOLO orchestrator supports both force_refresh and forceRefresh; we standardize on force_refresh.
    requestBody.force_refresh = true;
  }
  if (extra) {
    Object.assign(requestBody, extra);
  }

  const result = await fetchAPI<ScrapeResult>(API_CONFIG.ORCHESTRATOR_API, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
  return normalizeScrapeResult(result);
}

// Check scrape status
export async function checkStatus(
  runId: string,
  type: string
): Promise<StatusResult> {
  return fetchAPI<StatusResult>(API_CONFIG.ORCHESTRATOR_API, {
    method: 'POST',
    body: JSON.stringify({
      action: 'check_status',
      type,
      run_id: runId,
    }),
  });
}

// Get scrape results
export async function getResults(datasetId: string): Promise<unknown[]> {
  const response = await fetchAPI<{ items?: unknown[] }>(API_CONFIG.ORCHESTRATOR_API, {
    method: 'POST',
    body: JSON.stringify({
      action: 'get_results',
      dataset_id: datasetId,
    }),
  });
  return response.items || [];
}

export async function getResultsByRunId(runId: string): Promise<unknown[]> {
  const response = await fetchAPI<{ items?: unknown[] }>(API_CONFIG.ORCHESTRATOR_API, {
    method: 'POST',
    body: JSON.stringify({
      action: 'get_results',
      run_id: runId,
    }),
  });
  return response.items || [];
}

// Poll for results with timeout
export async function pollAndGetResults(
  runId: string,
  datasetId: string | undefined,
  actorType: string,
  onProgress?: (progress: number, message: string) => void,
  maxAttempts = 60
): Promise<unknown[]> {
  let status = 'RUNNING';
  let attempts = 0;

  while ((status === 'RUNNING' || status === 'READY') && attempts < maxAttempts) {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    attempts++;

    const statusResult = await checkStatus(runId, actorType);
    status = statusResult.status;

    const progress = Math.min(30 + (attempts / maxAttempts) * 60, 90);
    onProgress?.(progress, `Scraping ${actorType}... (${attempts * 3}s)`);

    if (status === 'SUCCEEDED') {
      break;
    }

    if (status === 'FAILED' || status === 'ABORTED') {
      throw new Error(`Scrape ${status.toLowerCase()}`);
    }
  }

  if (attempts >= maxAttempts) {
    throw new Error('Scrape timeout');
  }

  if (!datasetId) {
    throw new Error(`Missing dataset_id for ${actorType}`);
  }
  return getResults(datasetId);
}

// Async helpers (start + poll) so UI can run scrapes in the background
export async function startLinkedIn360Scrape(
  companyName: string,
  linkedinUrl: string,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<ScrapeResult> {
  return startScrape('linkedin_360', companyName, linkedinUrl, userEmail, forceRefresh);
}

export async function pollLinkedIn360Results(
  result: ScrapeResult,
  onProgress?: (progress: number, message: string) => void,
  options?: { requireCompanyProfile?: boolean }
): Promise<{ company: unknown[]; posts: unknown[]; jobs: unknown[] }> {
  const allResults: { company: unknown[]; posts: unknown[]; jobs: unknown[] } = {
    company: [],
    posts: [],
    jobs: [],
  };

  const safePoll = async (
    runId: string,
    datasetId: string | undefined,
    actorType: string,
    progress: number,
    label: string
  ) => {
    try {
      onProgress?.(progress, label);
      return (await pollAndGetResults(runId, datasetId, actorType, onProgress)) as unknown[];
    } catch (e) {
      console.warn(`LinkedIn ${actorType} failed:`, e);
      return [];
    }
  };

  if (result.company?.run_id) {
    allResults.company = await safePoll(
      result.company.run_id,
      result.company.dataset_id,
      'linkedin_company',
      20,
      'Scraping company data...'
    );
  }

  if (result.posts?.run_id) {
    allResults.posts = await safePoll(
      result.posts.run_id,
      result.posts.dataset_id,
      'linkedin_posts',
      40,
      'Scraping posts...'
    );
  }

  if (result.jobs?.run_id) {
    allResults.jobs = await safePoll(
      result.jobs.run_id,
      result.jobs.dataset_id,
      'linkedin_jobs',
      60,
      'Scraping jobs...'
    );
  }

  const requireCompanyProfile = options?.requireCompanyProfile !== false;
  if (requireCompanyProfile) {
    if (allResults.company.length === 0) {
      throw new Error('Scrape failed: missing company profile');
    }
  } else if (allResults.company.length === 0 && allResults.posts.length === 0 && allResults.jobs.length === 0) {
    throw new Error('Scrape failed');
  }

  return allResults;
}

export async function startGlassdoorScrape(
  companyName: string,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<ScrapeResult> {
  return startScrape('glassdoor', companyName, undefined, userEmail, forceRefresh);
}

export async function pollGlassdoorResults(
  result: ScrapeResult,
  onProgress?: (progress: number, message: string) => void
): Promise<{
  overview: unknown[];
  reviews: unknown[];
  salaries: unknown[];
  interviews: unknown[];
  benefits: unknown[];
  company_slug?: string;
  e_id?: string;
}> {
  const allResults: {
    overview: unknown[];
    reviews: unknown[];
    salaries: unknown[];
    interviews: unknown[];
    benefits: unknown[];
    company_slug?: string;
    e_id?: string;
  } = {
    overview: [],
    reviews: [],
    salaries: [],
    interviews: [],
    benefits: [],
    company_slug: (result as unknown as { company_slug?: string }).company_slug,
    e_id: (result as unknown as { e_id?: string }).e_id,
  };

  if (result.overview?.run_id) {
    onProgress?.(15, 'Scraping overview...');
    allResults.overview = (await pollAndGetResults(
      result.overview.run_id,
      result.overview.dataset_id,
      'glassdoor',
      onProgress
    )) as unknown[];
  }

  if (result.reviews?.run_id) {
    onProgress?.(30, 'Scraping reviews...');
    allResults.reviews = (await pollAndGetResults(
      result.reviews.run_id,
      result.reviews.dataset_id,
      'glassdoor',
      onProgress
    )) as unknown[];
  }

  if (result.salaries?.run_id) {
    onProgress?.(50, 'Scraping salaries...');
    allResults.salaries = (await pollAndGetResults(
      result.salaries.run_id,
      result.salaries.dataset_id,
      'glassdoor',
      onProgress
    )) as unknown[];
  }

  if (result.interviews?.run_id) {
    onProgress?.(70, 'Scraping interviews...');
    allResults.interviews = (await pollAndGetResults(
      result.interviews.run_id,
      result.interviews.dataset_id,
      'glassdoor',
      onProgress
    )) as unknown[];
  }

  if (result.benefits?.run_id) {
    onProgress?.(85, 'Scraping benefits...');
    allResults.benefits = (await pollAndGetResults(
      result.benefits.run_id,
      result.benefits.dataset_id,
      'glassdoor',
      onProgress
    )) as unknown[];
  }

  return allResults;
}

export async function startConsumerIntelScrape(
  companyName: string,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<ScrapeResult> {
  return startScrape('consumer_intel', companyName, undefined, userEmail, forceRefresh);
}

export async function startG2Scrape(
  companyName: string,
  userEmail?: string,
  forceRefresh?: boolean,
  domain?: string
): Promise<ScrapeResult> {
  return startScrape('g2_reviews', companyName, undefined, userEmail, forceRefresh, domain ? { domain } : undefined);
}

export async function pollG2Results(
  result: ScrapeResult,
  onProgress?: (progress: number, message: string) => void
): Promise<unknown[]> {
  if (!result.run_id) throw new Error('No run ID returned');
  return pollAndGetResults(result.run_id, result.dataset_id, 'g2_reviews', onProgress, 120);
}

export async function startTrustpilotScrape(
  companyName: string,
  userEmail?: string,
  forceRefresh?: boolean,
  domain?: string
): Promise<ScrapeResult> {
  return startScrape('trustpilot', companyName, undefined, userEmail, forceRefresh, domain ? { domain } : undefined);
}

export async function pollTrustpilotResults(
  result: ScrapeResult,
  onProgress?: (progress: number, message: string) => void
): Promise<unknown[]> {
  if (!result.run_id) throw new Error('No run ID returned');
  return pollAndGetResults(result.run_id, result.dataset_id, 'trustpilot', onProgress, 120);
}

export async function pollConsumerIntelResults(
  result: ScrapeResult,
  onProgress?: (progress: number, message: string) => void
): Promise<{ trustpilot: unknown[]; g2: unknown[] }> {
  const allResults: { trustpilot: unknown[]; g2: unknown[] } = {
    trustpilot: [],
    g2: [],
  };

  if (result.results?.trustpilot?.run_id) {
    onProgress?.(30, 'Scraping Trustpilot...');
    allResults.trustpilot = (await pollAndGetResults(
      result.results.trustpilot.run_id,
      result.results.trustpilot.dataset_id,
      'trustpilot',
      onProgress,
      120
    )) as unknown[];
  }

  if (result.results?.g2?.run_id) {
    onProgress?.(70, 'Scraping G2...');
    allResults.g2 = (await pollAndGetResults(
      result.results.g2.run_id,
      result.results.g2.dataset_id,
      'g2_reviews',
      onProgress,
      120
    )) as unknown[];
  }

  return allResults;
}

export async function startCrunchbaseScrape(
  companyName: string,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<ScrapeResult> {
  return startScrape('crunchbase', companyName, undefined, userEmail, forceRefresh);
}

export async function pollCrunchbaseResults(
  result: ScrapeResult,
  onProgress?: (progress: number, message: string) => void
): Promise<unknown[]> {
  if (!result.run_id || !result.dataset_id) {
    throw new Error('No run ID returned');
  }

  return pollAndGetResults(
    result.run_id,
    result.dataset_id,
    'crunchbase',
    onProgress
  );
}

// Scrape LinkedIn 360 (company + posts + jobs)
export async function scrapeLinkedIn360(
  companyName: string,
  linkedinUrl: string,
  onProgress?: (progress: number, message: string) => void,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<{ company: unknown[]; posts: unknown[]; jobs: unknown[] }> {
  const result = await startScrape('linkedin_360', companyName, linkedinUrl, userEmail, forceRefresh);

  if (!result.success) {
    throw new Error(result.error || 'Failed to start LinkedIn scrape');
  }

  return pollLinkedIn360Results(result, onProgress);
}

// Scrape Glassdoor (overview + reviews + salaries + interviews + benefits)
export async function scrapeGlassdoor(
  companyName: string,
  onProgress?: (progress: number, message: string) => void,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<{
  overview: unknown[];
  reviews: unknown[];
  salaries: unknown[];
  interviews: unknown[];
  benefits: unknown[];
  company_slug?: string;
  e_id?: string;
}> {
  const result = await startScrape('glassdoor', companyName, undefined, userEmail, forceRefresh);

  if (!result.success) {
    throw new Error(result.error || 'Failed to start Glassdoor scrape');
  }

  return pollGlassdoorResults(result, onProgress);
}

// Scrape Consumer Intel (Trustpilot + G2)
export async function scrapeConsumerIntel(
  companyName: string,
  onProgress?: (progress: number, message: string) => void,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<{ trustpilot: unknown[]; g2: unknown[] }> {
  const result = await startScrape('consumer_intel', companyName, undefined, userEmail, forceRefresh);

  if (!result.success) {
    throw new Error(result.error || 'Failed to start Consumer Intel scrape');
  }

  return pollConsumerIntelResults(result, onProgress);
}

// Scrape Crunchbase
export async function scrapeCrunchbase(
  companyName: string,
  onProgress?: (progress: number, message: string) => void,
  userEmail?: string,
  forceRefresh?: boolean
): Promise<unknown[]> {
  const result = await startScrape('crunchbase', companyName, undefined, userEmail, forceRefresh);

  if (!result.success) {
    throw new Error(result.error || 'Failed to start Crunchbase scrape');
  }

  return pollCrunchbaseResults(result, onProgress);
}

// Push data to Make.com webhook
export async function pushToMake(
  company: Company,
  intel: Record<string, unknown>
): Promise<boolean> {
  const payload = {
    companyId: company.companyId,
    companyName: company.companyName,
    phone: company.phone || null,
    email: company.email || null,
    linkedin: company.linkedin || null,
    website: company.website || null,
    contacts: company.contacts || [],
    ...intel,
    pushedAt: new Date().toISOString(),
  };

  const response = await fetch(API_CONFIG.MAKE_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return response.ok;
}
